<template>
  <v-dialog
    v-model="dialog"
    scrollable
    max-width="90vw"
    @click:outside="discardItem"
  >
    <v-card>
      <v-card-title class="d-flex align-start justify-space-between">
        <p>{{ title }}</p>
        <v-btn icon @click="discardItem">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text class="better-scrollbar pa-0">
        <v-container fluid>
          <v-row no-gutters>
            <v-col cols="12" md="6" class="pa-2">
              <v-text-field
                v-model="object.name"
                :label="$t('pointsOfInterest.name')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('name')"
                :error-messages="getErrors('name')"
              />
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-text-field
                v-model="object.url"
                :label="$t('pointsOfInterest.url')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('url')"
                :error-messages="getErrors('url')"
              />
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-text-field
                v-model="object.phone"
                :label="$t('pointsOfInterest.phone')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('phone')"
                :error-messages="getErrors('phone')"
              />
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-text-field
                v-model="object.sms"
                :label="$t('pointsOfInterest.sms')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('sms')"
                :error-messages="getErrors('sms')"
              />
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-text-field
                v-model="object.email"
                :label="$t('pointsOfInterest.email')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('email')"
                :error-messages="getErrors('email')"
              />
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-select
                v-model="object.category"
                :items="categories"
                item-text="text"
                item-value="value"
                :label="$t('pointsOfInterest.categories.title')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('category')"
                :error-messages="getErrors('category')"
              >
                <!-- eslint-disable-next-line vue/no-template-shadow -->
                <template slot="selection" slot-scope="data">
                  <v-icon>{{ data.item.icon }}</v-icon>{{ data.item.text }}
                </template>
                <!-- eslint-disable-next-line vue/no-template-shadow -->
                <template slot="item" slot-scope="data">
                  <v-icon>{{ data.item.icon }}</v-icon>{{ data.item.text }}
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" md="6" class="pa-2">
              <v-select
                v-model="object.courseId"
                :items="coursesItems"
                item-text="text"
                item-value="id"
                :label="$t('pointsOfInterest.course')"
                :disabled="!isEditting"
                outlined
                :error="hasErrors('courseId')"
                :error-messages="getErrors('courseId')"
              />
            </v-col>
            <v-col cols="12" md="12" class="pa-2">
              <v-textarea
                v-model="object.description"
                :label="$t('pointsOfInterest.description')"
                :disabled="!isEditting"
                outlined
                auto-grow
                :error="hasErrors('description')"
                :error-messages="getErrors('description')"
              />
            </v-col>
            <v-col cols="6">
              <v-row no-gutters>
                <v-col cols="12" class="pa-2">
                  <v-file-input
                    v-model="file"
                    outlined
                    :disabled="!isEditting"
                    :label="$t('pointsOfInterest.image')"
                    counter
                    prepend-icon="mdi-paperclip"
                    :show-size="1000"
                    :error="hasErrors('image')"
                    :error-messages="getErrors('image')"
                    @change="toBase64"
                  >
                    <template #selection="{ text }">
                      <v-chip
                        color="secondary black--text"
                        dark
                        label
                        small
                      >
                        {{ text }}
                      </v-chip>
                    </template>
                  </v-file-input>
                </v-col>
                <v-col cols="12" class="pa-2 d-flex justify-end">
                  <v-card v-if="file || object.image" width="100%" outlined>
                    <v-img
                      contain
                      aspect-ratio="1"
                      :src="object.image"
                    />
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6" class="pa-2">
              <v-card height="350px" elevation="0">
                <gm-map
                  v-model="object"
                  :show="dialog"
                  :disabled="!isEditting"
                  :dark="isDark"
                  map-id="courseMap"
                  width="100%"
                  height="350px"
                  :error="getErrors('point.latitude')"
                />
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="d-flex justify-space-between">
        <v-btn
          v-if="isEditting"
          :ripple="false"
          outlined
          dark
          color="primary"
          @click="discardItem"
        >
          {{ $t('actions.discard') }}
        </v-btn>
        <v-btn
          v-if="isEditting"
          :ripple="false"
          depressed
          color="primary"
          @click="saveItem(object.id)"
        >
          {{ $t('actions.save') }}
        </v-btn>
        <v-btn
          v-if="!isEditting"
          :ripple="false"
          depressed
          color="orange darken-1 white--text"
          @click="isEditting = true"
        >
          {{ $t('actions.edit') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'
import { orm } from '@/mixins'
import addMutation from '@/graphql/mutations/pointsOfInterest/add.gql'
import editMutation from '@/graphql/mutations/pointsOfInterest/edit.gql'

import Maps from './Maps.vue'

export default {
  components: {
    'gm-map': Maps
  },

  mixins: [orm],
  model: {
    prop: 'data',
    event: 'set-object'
  },

  props: {
    showDialog: { type: Boolean, default: () => false },
    data: { type: Object, default: () => {} },
    readonly: {
      type: Boolean,
      default: () => false
    }
  },

  data () {
    return {
      show: false,
      errors: {},
      file: null
    }
  },

  computed: {
    ...mapState(['isDark', 'users', 'pointsOfInterest', 'courses']),
    object: {
      get () {
        return this.data
      },

      set (value) {
        this.$emit('toggle-object', value)
      }
    },

    dialog: {
      get () {
        return this.showDialog
      },

      set (value) {
        this.$emit('toggle-dialog', value)
      }
    },

    isEditting: {
      get () {
        return !this.readonly
      },

      set (value) {
        this.$emit('set-readonly', value)
      }
    },

    title () {
      return this.object.id === undefined ? this.$i18n.t('pointsOfInterest.new') : this.$i18n.t('pointsOfInterest.edit')
    },

    categories () {
      return [
        {
          icon: 'mdi-store',
          text: this.$i18n.t('pointsOfInterest.categories.shop'),
          value: 'SHOP'
        },
        {
          icon: 'mdi-silverware',
          text: this.$i18n.t('pointsOfInterest.categories.restaurant'),
          value: 'RESTAURANT'
        },
        {
          icon: 'mdi-bed',
          text: this.$i18n.t('pointsOfInterest.categories.hotel'),
          value: 'HOTEL'
        },
        {
          icon: 'mdi-golf',
          text: this.$i18n.t('pointsOfInterest.categories.others'),
          value: 'OTHERS'
        }
      ]
    },

    coursesItems () {
      return this.courses.list.filter(el => el.parentId === null).map(el => {
        return {
          id: el.id,
          text: el.name
        }
      })
    }
  },
  methods: {
    async toBase64 (file) {
      if (!file) {
        this.object.image = ''
        return
      }

      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => {
        const result = reader.result

        if (
          result.startsWith('data:image/png') ||
          result.startsWith('data:image/jpeg') ||
          result.startsWith('data:image/svg+xml') ||
          result.startsWith('data:image/webp') ||
          result.startsWith('data:image/gif')
        ) {
          this.object.image = result
        } else {
          this.$store.commit('toggleSnackbar', {
            message: this.$t('helpers.errors.invalidFormat'),
            color: 'orange darken-2'
          })
        }
      }
    },

    async saveItem (id) {
      this.isLoading = true
      let image = null
      if (this.file) {
        image = this.object.image
      }
      const data = {
        id: id,
        name: this.object.name,
        phone: this.object.phone,
        sms: this.object.sms,
        url: this.object.url,
        email: this.object.email,
        description: this.object.description,
        category: this.object.category,
        courseId: this.object.courseId,
        image,
        point: this.object.point
      }

      delete data.point.__typename

      if (id) {
        await this.edit(data)
      } else {
        await this.add(data)
      }
    },

    evaluateResponse ({ status, errors, result, action }) {
      this.isLoading = false
      console.log(status, errors)
      switch (status) {
        case 'OK':
          // this.discardItem()
          this.resetErrors()
          this.isEditting = false
          this.$store.commit('toggleSnackbar', {
            color: 'green darken-2',
            message: this.$t('helpers.save.success')
          })
          if (action === 'add' || action === 'edit') {
            this.$store.dispatch('pointsOfInterest/getList', true)
          }
          break
        case 'UNPROCESSABLE':
          this.errors = this.parseErrors(errors)
          this.$store.commit('toggleSnackbar', {
            color: 'orange darken-2',
            message: this.$t('errors.invalidFields')
          })
          break
        case 'BADREQUEST':
          this.errors = this.parseErrors(errors)
          this.$store.commit('toggleSnackbar', {
            color: 'orange darken-2',
            message: this.$t('errors.invalidFields')
          })
          break
        case 'LIMITREACHED':
          this.$store.commit('toggleSnackbar', {
            color: 'orange darken-2',
            message: this.$t('errors.limit_reached')
          })
          break
      }
    },

    add (data) {
      const action = 'add'
      this.$apollo.mutate({
        mutation: addMutation,
        variables: {
          token: this.users.entity.token,
          data
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        const { status, errors, result } = response.data.addPointOfInterest
        this.evaluateResponse({ status, errors, result, action })
      }).catch(err => {
        console.log('[Devices Dialog] Error handled', err)
        this.$store.commit('toggleSnackbar', {
          color: 'red darken-2',
          message: this.$t('helpers.errors.disaster')
        })
      })
    },

    edit (data) {
      const action = 'edit'
      this.$apollo.mutate({
        mutation: editMutation,
        variables: {
          token: this.users.entity.token,
          data
        },
        fetchPolicy: 'no-cache'
      }).then(response => {
        const { status, errors, result } = response.data.editPointOfInterest
        this.evaluateResponse({ status, errors, result, action })
      }).catch(err => {
        console.log('[Devices Dialog] Error handled', err)
        this.$store.commit('toggleSnackbar', {
          color: 'red darken-2',
          message: this.$t('helpers.errors.disaster')
        })
      })
    },

    discardItem () {
      this.dialog = false
      this.file = null
      this.resetErrors()
      this.object = Object.assign({}, this.$store.getters['pointsOfInterest/defaultObject'])
    }
  }
}
</script>
